import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUnpaidClientInvoices } from '../services/invoices';
import './UnpaidInvoicesBanner.css';

export default function UnpaidInvoicesBanner({ clientId, onInvoicePaid }) {
  const [unpaidInvoices, setUnpaidInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUnpaidInvoices = useCallback(async () => {
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const invoices = await getUnpaidClientInvoices(clientId);
      setUnpaidInvoices(invoices);
    } catch (error) {
      console.error('Error loading unpaid invoices:', error);
      setUnpaidInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Reload when invoice is marked as paid (if callback provided)
  useEffect(() => {
    if (typeof onInvoicePaid === 'function') {
      loadUnpaidInvoices();
    }
  }, [onInvoicePaid, loadUnpaidInvoices]);

  useEffect(() => {
    loadUnpaidInvoices();
  }, [loadUnpaidInvoices]);

  // Listen for invoice paid events
  useEffect(() => {
    const handleInvoicePaid = () => {
      loadUnpaidInvoices();
    };
    
    window.addEventListener('invoicePaid', handleInvoicePaid);
    return () => {
      window.removeEventListener('invoicePaid', handleInvoicePaid);
    };
  }, [loadUnpaidInvoices]);

  const navigate = useNavigate();

  if (loading || unpaidInvoices.length === 0) {
    return null;
  }

  const handleClick = () => {
    // Navigate to the first unpaid invoice
    const firstInvoiceId = unpaidInvoices.length > 0 ? unpaidInvoices[0].id : null;
    navigate(`/clients/${clientId}`, { state: { activeTab: 'history', invoiceId: firstInvoiceId } });
  };

  return (
    <div className="unpaid-invoices-banner">
      <div className="banner-content">
        <span className="banner-icon">⚠️</span>
        <span className="banner-text">
          {unpaidInvoices.length === 1 
            ? 'There is 1 unpaid invoice for this client'
            : `There are ${unpaidInvoices.length} unpaid invoices for this client`
          }
        </span>
        <button className="banner-link" onClick={handleClick}>
          View Invoices →
        </button>
      </div>
    </div>
  );
}
